// AppConfigContext.tsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { getApplicationConfig } from "../network/apiService";
import { getCookie } from "../utils/cookies";
import eventEmitter from "../eventEmitter";

const APP_CONFIG_STORAGE_KEY = "tc_application_config";

interface AppConfigContextType {
  config: any;
  loading: boolean;
  error: Error | null;
}

const AppConfigContext = createContext<AppConfigContextType>({
  config: null,
  loading: false,
  error: null
});

export const useAppConfig = () => useContext(AppConfigContext);

function readCachedConfig(): any | null {
  try {
    const raw = sessionStorage.getItem(APP_CONFIG_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCachedConfig(nextConfig: any) {
  try {
    sessionStorage.setItem(
      APP_CONFIG_STORAGE_KEY,
      JSON.stringify(nextConfig ?? {})
    );
  } catch {
    // Ignore quota / private-mode failures; in-memory config still works.
  }
}

function clearCachedConfig() {
  try {
    sessionStorage.removeItem(APP_CONFIG_STORAGE_KEY);
  } catch {
    // no-op
  }
}

function getInitialConfig(): any | null {
  if (!getCookie("id_token")) {
    return null;
  }
  return readCachedConfig();
}

export const AppConfigProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [config, setConfig] = useState<any>(getInitialConfig);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const inFlightRef = useRef(false);
  const loadedRef = useRef(config != null);

  const fetchConfig = useCallback(async (force = false) => {
    if (!getCookie("id_token") || inFlightRef.current) {
      return;
    }
    if (!force && loadedRef.current) {
      return;
    }

    inFlightRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const res: any = await getApplicationConfig();
      const nextConfig = res?.data ?? {};
      setConfig(nextConfig);
      writeCachedConfig(nextConfig);
      loadedRef.current = true;
    } catch (err: any) {
      setError(err);
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Cover hard reload / already-authenticated SPA entry.
    fetchConfig(false);

    // Cover login race: AuthCallback sets id_token then emits userLogin.
    const onUserLogin = () => {
      loadedRef.current = false;
      clearCachedConfig();
      fetchConfig(true);
    };

    eventEmitter.addEventListener("userLogin", onUserLogin);
    return () => {
      eventEmitter.removeEventListener("userLogin", onUserLogin);
    };
  }, [fetchConfig]);

  return (
    <AppConfigContext.Provider value={{ config, loading, error }}>
      {children}
    </AppConfigContext.Provider>
  );
};
